import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/roof-leak-repair";
const NAME = "Roof Leak Repair";
const TITLE = "Roof Leak Repair Phoenix | We Find the Source | Durafoam";
const DESCRIPTION =
  "Roof leak repair in Phoenix, AZ. We trace the leak to its actual source, fix it right, and back it in writing. Free inspection, fast response since 1989.";


const faqs = [
  {
    q: "How do I know if I have a roof leak?",
    a: "Common signs: brown ceiling stains, soft or sagging drywall, peeling paint near walls or ceilings, musty smells in upper rooms, and visible drips during monsoon storms. Sometimes leaks show up far from the actual source on the roof.",
  },
  {
    q: "Why does my ceiling leak in a spot that's nowhere near a pipe?",
    a: "Water travels along the underside of the roof deck or along framing before it finds an opening to drip through. The visible drip can be 20+ feet from the actual roof failure. That's why guessing at the patch location rarely works.",
  },
  {
    q: "Should I tarp my roof before you arrive?",
    a: "If you can do it safely from the ground or via a stable ladder — yes. But never get on a tile or foam roof if you're not trained; tile cracks under the wrong foot placement and you can punch through soft foam. We'd rather come tarp it for you than treat a fall.",
  },
  {
    q: "How long does a typical leak repair take?",
    a: "Most single-source leaks are repaired in a half-day to a day. Multi-point failures or repairs requiring substantial dry-time may take 2–3 days.",
  },
  {
    q: "Will my insurance cover roof leak repair?",
    a: "Storm-driven damage from a monsoon or hail event is often covered; gradual wear-out is generally not. We can document conditions for your claim, but we don't file it for you.",
  },
  {
    q: "How much does it cost to repair a roof leak?",
    a: "Most single-source leak repairs in Phoenix run a few hundred to low-thousand dollars depending on roof type and access — a flashing rebuild is very different from re-sloping a ponding area. See our roof repair cost guide for real ranges by roof type, or book a free inspection for an exact written price.",
    aNode: <>Most single-source leak repairs in Phoenix run a few hundred to low-thousand dollars depending on roof type and access — a flashing rebuild is very different from re-sloping a ponding area. See our{" "}<Link to="/roof-repair-cost-phoenix" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">roof repair cost guide</Link>{" "}for real ranges by roof type, or book a free inspection for an exact written price.</>,
  },
  {
    q: "Can a roof leak be repaired, or do I need a new roof?",
    a: "Repair, in most cases. A leak is almost always one specific failure — a flashing, a seam, a penetration — not the whole roof giving up. Replacement only makes sense when the system is at end of life across the board. We'll show you photos of what we find and tell you honestly which case you're in.",
  },
];

export const Route = createFileRoute("/roof-leak-repair")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="roof-leak-repair"
      extraSections={SEARCH_TERM_SECTIONS["roof-leak-repair"]}
      eyebrow="Leak Repair"
      h1="Roof Leak Repair in Phoenix"
      subhead="We find the actual source of the leak — not the spot above the stain — and fix it so it stays fixed. Foam, tile, shingle, and flat roofs."
      heroBullets={[
        "Source-traced, not guess-patched",
        "Fast response — we get to repair calls ASAP",
        "Written estimate before any work",
        "Workmanship warranty on every leak repair",
      ]}
      whatIs={{
        heading: "Why most leak repairs fail",
        body: (
          <>
            <p className="font-semibold text-foreground">
              Short answer: roof leak repair in Phoenix starts with finding where water actually
              enters the roof, which is rarely above the stain on your ceiling. Most leaks trace to
              one failure — a pipe boot, a slipped tile, a wall flashing, a worn foam coating, or a
              skylight — and most are repaired in a half-day to a day.
            </p>
            <p>

              The number-one reason a "repaired" leak comes back is that the original repair was
              done at the wrong spot. Water enters through an opening on the roof, runs sideways
              along the deck or framing, and drips through wherever it finds the easiest exit.
              That exit can be five, ten, or twenty feet from the actual roof failure.
            </p>
            <p>
              A real leak repair starts with diagnosis. We go onto the roof and trace the leak
              path back to the entry point — usually a failed pipe boot, a slipped tile, a cracked
              flashing, a thinned coating, or a damaged vent. Once we identify the source, we fix
              it with the right material for that specific failure.
            </p>
            <p>
              Most leaks have a single source. We don't sell you a coating job when you need a
              pipe boot, and we don't sell you a re-roof when you need a flashing. That discipline
              is why we have customers from 1989 still calling us. Curious about pricing? You can{" "}
              <Link
                to="/roof-repair-cost-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                see typical Phoenix roof repair cost ranges
              </Link>{" "}
              before scheduling an inspection.
            </p>

          </>
        ),
      }}
      climate={{
        heading: "Phoenix leak patterns",
        body: (
          <>
            <p>
              Almost every leak we see in the Valley falls into one of a few categories. Knowing
              the pattern speeds up the diagnosis and gets your roof watertight faster.
            </p>
            <ul>
              <li>
                <strong>Pipe boots:</strong> The rubber gasket around your plumbing vents bakes
                hard and cracks in 7–10 years here. It's the most common leak source on Phoenix
                homes by a wide margin.
              </li>
              <li>
                <strong>Tile slips and fractures:</strong> Foot traffic from HVAC techs and
                satellite installers shifts and cracks tile, exposing the underlayment below.
              </li>
              <li>
                <strong>Failed coatings on foam:</strong> UV-aged coatings let water through to
                the foam, where it then tracks along the substrate before dripping.
              </li>
              <li>
                <strong>Wall flashings:</strong> Counterflashing and step flashing at parapet
                walls and chimneys move with thermal cycling and eventually open up.
              </li>
              <li>
                <strong>Skylights:</strong> Old skylight gaskets, curb flashings, and condensation
                are commonly mistaken for roof leaks.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our leak repair process",
        steps: [
          {
            title: "Free leak diagnosis",
            body: "We trace the leak to its actual source on the roof — not just the stain inside. You see photos of the failure.",
          },
          {
            title: "Written, scoped estimate",
            body: "Repair scope, materials, and price documented before any work starts. If we find more than one issue, you decide what to address.",
          },
          {
            title: "Right repair, right material",
            body: "Pipe boots, flashings, foam patches, tile resets — each failure gets the standard manufacturer-approved repair for that roof type.",
          },
          {
            title: "Test and warrant",
            body: "We water-test where appropriate, walk you through the finished repair, and back it with a written workmanship warranty.",
          },
        ],
      }}
      extraSection={{
        heading: "Leak calls we run every week",
        body: (
          <>
            <p>
              Leaks don't respect city limits — during monsoon we're running calls across the whole Valley.
              Frequent stops include{" "}
              <Link to="/phoenix-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Phoenix from Sunnyslope to South Mountain</Link>,{" "}
              <Link to="/tempe-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Tempe rentals near ASU</Link>,{" "}
              <Link to="/gilbert-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Gilbert's newer subdivisions</Link>{" "}
              where tile underlayment is aging out, and{" "}
              <Link to="/sun-city-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Sun City and Sun City West</Link>{" "}
              for foam recoat-related leaks. If you have an active drip right now, we also cover{" "}
              <Link to="/emergency-roof-leak-repair" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">same-day emergency leak response</Link>{" "}
              most weeks.
            </p>
          </>
        ),
      }}
      testimonial={{
        quote:
          "Brad and his team were amazing! They responded immediately to my request and got me scheduled right away. They came as promised, and they did an amazing job for a very reasonable price. I was VERY impressed by the professionalism. Thank you Brad and team!",
        name: "Suzanne Juneau",
        source: "Google review",
        context: "Roof repair for storm & wind damage",
      }}
      processFirst
      ctaLabel="Book a Leak Inspection Now"
      faqs={faqs}
      ctaTitle="Ceiling stain getting worse?"
      ctaBody="Don't wait for the next monsoon. Schedule a free leak inspection and we'll find the source."
    />
  );
}
